import { isWebsiteBuilderStretchContainerData, TWebsiteBuilderContainer } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderContainer";
import { TWebsiteBuilderContextMenuSection, TWebsiteBuilderContextMenuSections } from "../../helpers/WebsiteBuilderContextMenu";
import { TWebsiteBuilderSection } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderSection";
import { useTranslation } from "react-i18next";
import { useWebsiteBuilderEditorContext } from "pages/secure/facility/websiteBuilder/editor/websiteBuilderEditorContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp } from "@fortawesome/pro-regular-svg-icons";
import React from "react";
import { useAppSelector } from "hooks/redux";
import { websiteBuilderCanBringToBackInFreeformParent, websiteBuilderCanBringToFrontInFreeformParent } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderFreeformParentNode";

export interface IUseContainerViewContextMenuContentArgs {
  node: TWebsiteBuilderContainer;
  parentNode: TWebsiteBuilderSection;
  menuActive: boolean;
  openStretchSettings: () => void;
}

interface IUseContainerViewContextMenuContent {
  sections: TWebsiteBuilderContextMenuSections;
}

export function useContainerViewContextMenuContent(args: IUseContainerViewContextMenuContentArgs): IUseContainerViewContextMenuContent {
  const { t } = useTranslation();

  const { activePageDomNodeRefMap, tabsetActiveTabMap, scrollContainerRef, containerOperations, clipboard } = useWebsiteBuilderEditorContext();

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  
  const modifierKey = window.navigator.userAgent.includes("Mac") ? "⌘" : "Ctrl";

  function getSections(): TWebsiteBuilderContextMenuSections {
    const sections: TWebsiteBuilderContextMenuSections = [];

    if (!args.menuActive) {
      return sections;
    }
  
    if (isWebsiteBuilderStretchContainerData(args.node.container)) {
      sections.push([
        {
          label: t("Stretch"),
          shortcut: "",
          onClick: args.openStretchSettings,
          skipCloseAfterClick: true,
        },
      ]);
    }

    const creationMenuSection: TWebsiteBuilderContextMenuSection = [
      {
        label: t("Copy"),
        shortcut: `${modifierKey} + C`,
        onClick: () => containerOperations.copyContainer(args.node),
      },
      {
        label: t("Paste"),
        shortcut: `${modifierKey} + V`,
        onClick: () => containerOperations.pasteAtContainer(
          args.node,
          args.parentNode,
          activePageId,
          deviceView,
          tabsetActiveTabMap,
          activePageDomNodeRefMap,
          scrollContainerRef,
        ),
        disabled: clipboard.copyType !== "element" && clipboard.copyType !== "container",
      },
      {
        label: t("Duplicate"),
        shortcut: `${modifierKey} + D`,
        onClick: () => containerOperations.duplicateContainer(args.node, args.parentNode, activePageId, deviceView),
      },
    ];
  
    const zLayerMenuSection: TWebsiteBuilderContextMenuSection = [
      {
        label: t("Bring to front"),
        shortcut: (
          <>
            {modifierKey} + <FontAwesomeIcon icon={faArrowUp} />
          </>
        ),
        onClick: () => containerOperations.bringToFrontContainer(args.node, args.parentNode, activePageId, deviceView),
        disabled: !websiteBuilderCanBringToFrontInFreeformParent(args.node, args.parentNode),
      },
      {
        label: t("Bring to back"),
        shortcut: (
          <>
            {modifierKey} + <FontAwesomeIcon icon={faArrowDown} />
          </>
        ),
        onClick: () => containerOperations.bringToBackContainer(args.node, args.parentNode, activePageId, deviceView),
        disabled: !websiteBuilderCanBringToBackInFreeformParent(args.node, args.parentNode),
      },
    ];
  
    const deletionMenuSection: TWebsiteBuilderContextMenuSection = [
      {
        label: t("Delete"),
        shortcut: "Del",
        onClick: () => containerOperations.deleteContainer(args.node, args.parentNode, activePageId, deviceView),
      },
      {
        label: t("Cut"),
        shortcut: `${modifierKey} + X`,
        onClick: () => containerOperations.cutContainer(args.node, args.parentNode, activePageId, deviceView),
      },
    ];

    sections.push(creationMenuSection, zLayerMenuSection, deletionMenuSection);

    return sections;
  }

  const sections = getSections();

  return {
    sections,
  };
}